import { createSortRule } from '../common/SortOrderModal';

export const sortByOptions = [
  { label: 'Unit Location', value: 'unitLocation', isObject: true, key: 'location', type: 'alpha' },
  { label: 'Manufacturer', value: 'manufacturer', isObject: true, key: 'factoryShortName', type: 'alpha' },
  { label: 'Number of Sections', value: 'numberOfSections', type: 'number', defaultValue: 1 },
  { label: 'Living Rooms', value: 'livingRooms', type: 'number' },
  { label: 'Floorplan Finance / Payoff', value: 'floorplanFinanceAmount', type: 'number' },
  { label: 'Invoice Amount', value: 'invoice', type: 'number', defaultValue: 0 },
  { label: 'Invoice Date', value: 'invoiceDate', type: 'date' },
  { label: 'Date of manufacture', value: 'dateOfManufacture', type: 'date' },
  { label: 'Date Created', value: 'createdAt', type: 'date' },
  { label: 'Last Updated', value: 'updatedAt', type: 'date' }
];

export const COLUMN_LABELS = {
  inventoryStatus: 'Inventory Status',
  curtailment: 'Curtailment',
  contractedBuyer: 'Contracted Buyer',
  contractStatus: 'Contract Status',
  potentialBuyers: 'Potential Buyers',
  unitLocation: 'Unit Location',
  manufacturer: 'Manufacturer',
  model: 'Model',
  maxSalesPrice: 'Max Sales Price',
  serials: 'Serials',
  sizeOverall: 'Size Overall',
  numberOfSections: 'Home Type',
  bedRooms: 'Bedrooms',
  bathRooms: 'Bathrooms',
  livingRooms: 'Living Rooms',
  floorplanLender: 'Floorplan Lender',
  dateOfManufacture: 'Date of manufacture',
  invoiceDate: 'Invoice Date',
  invoice: 'Invoice',
  floorplanFinanceAmount: 'Floorplan Finance / Payoff',
  slot: 'Slot'
};

export const COLUMN_WIDTHS = {
  unitLocation: 100,
  manufacturer: 100,
  bedRooms: 50,
  bathRooms: 50,
  floorplanLender: 120,
  invoice: 120
};

export const BROWSE_PRESETS = {
  browse: {
    title: 'Explore Inventory',
    statusOptions: ['Stock', 'RSO', 'Contracted', 'Stock On Order'],
    defaultStatus: ['Stock'],
    columns: Object.keys(COLUMN_LABELS),
    filterKeys: ['bedRooms', 'exteriorSiding', 'showCurtailmentUnitsOnly'],
    features: {
      copyAllRows: true,
      addUnit: true,
      quickToggles: true,
      showCurtailmentToggle: true,
      showUnsoldContractedUnits: true
    }
  },
  curtailments: {
    title: 'Explore Curtailments',
    statusOptions: ['Stock', 'RSO'],
    defaultStatus: ['Stock'],
    columns: Object.keys(COLUMN_LABELS).filter(
      key => !['maxSalesPrice', 'dateOfManufacture', 'slot'].includes(key)
    ),
    filterKeys: [],
    features: {
      copyAllRows: false,
      addUnit: true,
      quickToggles: false,
      showCurtailmentToggle: false,
      showUnsoldContractedUnits: true
    }
  }
};

export const getManagementSortRules = (oldestFirst = false) => [
  createSortRule('numberOfSections', 'asc'),
  createSortRule(oldestFirst ? 'invoiceDate' : 'invoice', 'asc'),
  createSortRule(oldestFirst ? 'invoice' : 'invoiceDate', 'asc')
];

export const getDefaultSortRules = () => [createSortRule('unitLocation', 'asc')];

export const buildFilterOptions = appChoices => {
  const salesLocationOptions =
    appChoices.find(ac => ac.key === 'salesLocation')?.values.map(v => ({ option: v.location, value: v._id })) || [];
  const manufacturerOptions =
    appChoices.find(ac => ac.key === 'manufacturer')?.values.map(v => ({ option: v.factoryShortName, value: v._id })) ||
    [];
  const floorplanLenderOptions =
    appChoices.find(ac => ac.key === 'floorplanLender')?.values.map(v => ({ option: v.name, value: v._id })) || [];
  const bedroomCountOptions = Array.from({ length: 10 }, (_, i) => i + 1).map(v => ({ option: v, value: v }));
  const exteriorSidingOptions =
    appChoices.find(ac => ac.key === 'Exterior Siding')?.values.map(v => ({ option: v, value: v })) || [];

  return {
    salesLocationOptions,
    manufacturerOptions,
    floorplanLenderOptions,
    bedroomCountOptions,
    exteriorSidingOptions,
    salesLocationDefaults: salesLocationOptions.map(o => o.value),
    manufacturerDefaults: manufacturerOptions.map(o => o.value),
    floorplanLenderDefaults: floorplanLenderOptions.map(o => o.value),
    bedRoomCountDefaults: bedroomCountOptions.map(o => o.value),
    exteriorSidingDefaults: exteriorSidingOptions.map(o => o.value)
  };
};

export const getVisibleColumns = ({
  preset,
  includeContractHeaders,
  includeContactHeaders
}) => {
  const presetConfig = BROWSE_PRESETS[preset] || BROWSE_PRESETS.browse;
  return presetConfig.columns.filter(columnKey => {
    if (columnKey === 'contractedBuyer' || columnKey === 'contractStatus') {
      return includeContractHeaders;
    }
    if (columnKey === 'potentialBuyers') {
      return includeContactHeaders;
    }
    return true;
  });
};

export const buildBrowseFiltersPayload = ({
  preset,
  status,
  salesLocation,
  salesLocationOptions,
  manufacturer,
  manufacturerOptions,
  floorplanLender,
  floorplanLenderOptions,
  bedRoomCount,
  bedroomCountOptions,
  exteriorSiding,
  exteriorSidingOptions,
  showUnsoldContractedUnits,
  showCurtailmentUnitsOnly,
  sectionCount = [],
  livingRoomCount = [],
  curtailmentAge = 'all'
}) => {
  const toFilterValue = (selected, options) =>
    selected.length === options.length ? 'all' : selected;

  const filters = {
    numberOfSections: sectionCount.length ? sectionCount : 'all',
    livingRooms: livingRoomCount.length ? livingRoomCount : 'all',
    curtailmentAge,
    inventoryStatus: status,
    unitLocation: toFilterValue(salesLocation, salesLocationOptions),
    manufacturer: toFilterValue(manufacturer, manufacturerOptions),
    floorplanLender: toFilterValue(floorplanLender, floorplanLenderOptions),
    showUnsoldContractedUnits
  };

  const presetConfig = BROWSE_PRESETS[preset] || BROWSE_PRESETS.browse;

  if (presetConfig.filterKeys.includes('bedRooms')) {
    filters.bedRooms = toFilterValue(bedRoomCount, bedroomCountOptions);
  }
  if (presetConfig.filterKeys.includes('exteriorSiding')) {
    filters.exteriorSiding = toFilterValue(exteriorSiding, exteriorSidingOptions);
  }
  if (presetConfig.filterKeys.includes('showCurtailmentUnitsOnly')) {
    filters.showCurtailmentUnitsOnly = showCurtailmentUnitsOnly;
  }

  return filters;
};
